import type { EventView } from "@confluence/shared";
import { addDays, dayKeyIn, daysBetween, minutesIn, type DayKey } from "./time";

const DAY_MIN = 24 * 60;
/** Hauteur minimale d'un événement dans la grille, en minutes, pour rester lisible. */
const MIN_SPAN = 22;

interface Span {
  first: DayKey;
  /** Dernier jour occupé (inclus). */
  last: DayKey;
  /** Affiché en bandeau : journée entière, ou 24 h et plus. */
  banner: boolean;
}

const spans = new WeakMap<EventView, Span>();

export function spanOf(ev: EventView, tz: string): Span {
  const cached = spans.get(ev);
  if (cached) return cached;
  let span: Span;
  if (ev.allDay) {
    // Journée entière : dates UTC, fin exclusive.
    const first = ev.start.slice(0, 10);
    const end = ev.end.slice(0, 10);
    span = { first, last: end > first ? addDays(end, -1) : first, banner: true };
  } else {
    const start = new Date(ev.start).getTime();
    const end = Math.max(new Date(ev.end).getTime(), start);
    span = {
      first: dayKeyIn(start, tz),
      last: dayKeyIn(end > start ? end - 1 : end, tz),
      banner: end - start >= DAY_MIN * 60_000,
    };
  }
  spans.set(ev, span);
  return span;
}

const covers = (s: Span, key: DayKey) => s.first <= key && key <= s.last;

/** Événements d'un jour : bandeaux d'abord, puis par heure de début. */
export function eventsOnDay(events: EventView[], key: DayKey, tz: string): EventView[] {
  return events
    .filter((e) => covers(spanOf(e, tz), key))
    .sort((a, b) => Number(spanOf(b, tz).banner) - Number(spanOf(a, tz).banner) || a.start.localeCompare(b.start));
}

export interface Segment {
  ev: EventView;
  /** Minutes depuis minuit. */
  top: number;
  bottom: number;
  col: number;
  cols: number;
}

/** Place les événements horaires d'un jour en colonnes quand ils se chevauchent. */
export function layoutDay(events: EventView[], key: DayKey, tz: string): Segment[] {
  const segs: Segment[] = [];
  for (const ev of events) {
    const s = spanOf(ev, tz);
    if (s.banner || !covers(s, key)) continue;
    const top = s.first === key ? minutesIn(ev.start, tz) : 0;
    const bottom = s.last === key && dayKeyIn(ev.end, tz) === key ? minutesIn(ev.end, tz) : DAY_MIN;
    segs.push({ ev, top, bottom: Math.min(DAY_MIN, Math.max(bottom, top + MIN_SPAN)), col: 0, cols: 1 });
  }
  segs.sort((a, b) => a.top - b.top || b.bottom - a.bottom);

  let cluster: Segment[] = [];
  let columns: number[] = [];
  let clusterEnd = -1;
  const close = () => {
    for (const seg of cluster) seg.cols = columns.length;
    cluster = [];
    columns = [];
  };
  for (const seg of segs) {
    if (seg.top >= clusterEnd) close();
    let col = columns.findIndex((end) => end <= seg.top);
    if (col === -1) {
      col = columns.length;
      columns.push(seg.bottom);
    } else columns[col] = seg.bottom;
    seg.col = col;
    cluster.push(seg);
    clusterEnd = Math.max(clusterEnd, seg.bottom);
  }
  close();
  return segs;
}

export interface Banner {
  ev: EventView;
  /** Index du premier et du dernier jour visibles (inclus). */
  from: number;
  to: number;
  lane: number;
  /** L'événement commence avant / finit après la plage affichée. */
  cutStart: boolean;
  cutEnd: boolean;
}

/** Bandeaux (journée entière, plusieurs jours) répartis sur des lignes sans chevauchement. */
export function layoutBanners(events: EventView[], days: DayKey[], tz: string): { banners: Banner[]; lanes: number } {
  const first = days[0]!;
  const last = days[days.length - 1]!;
  const items = events
    .map((ev) => ({ ev, s: spanOf(ev, tz) }))
    .filter(({ s }) => s.banner && s.first <= last && s.last >= first)
    .sort((a, b) => a.s.first.localeCompare(b.s.first) || daysBetween(b.s.first, b.s.last) - daysBetween(a.s.first, a.s.last));
  const laneEnds: number[] = [];
  const banners = items.map(({ ev, s }) => {
    const from = Math.max(0, daysBetween(first, s.first));
    const to = Math.min(days.length - 1, daysBetween(first, s.last));
    let lane = laneEnds.findIndex((end) => end < from);
    if (lane === -1) lane = laneEnds.push(to) - 1;
    else laneEnds[lane] = to;
    return { ev, from, to, lane, cutStart: s.first < first, cutEnd: s.last > last };
  });
  return { banners, lanes: laneEnds.length };
}
